import { useMemo, useState } from 'react'

import { useLightMode } from '@/hooks/useLightMode'
import type { TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'

import * as Sentry from '@sentry/react'

import {
  Box as DarkBox,
  Button as DarkButton,
  Card as DarkCard,
  Modal as DarkModal,
  Spinner as DarkSpinner,
  Text as DarkText,
  Pill,
  UtilityButton,
  useClipboard,
  useDisclosure,
} from '@hivewatch/dark'
import type { ColorScheme } from '@hivewatch/dark'

import { AiOperator } from '@/components/icons'
import { useIncidentAioEvalResultsLazyQuery } from '@/graphql/generated/hooks'
import type { IncidentAioEvalResultsQuery } from '@/graphql/generated/operations'
import { trackEvent } from '@/utils/analytics'

import { AIO_EVAL_RESULTS_OPENED } from '../../shared/constants/analyticsEvents'

const AiOperatorActionIcon = ({
  color,
  strokeColor,
}: {
  color?: string
  strokeColor?: string
}) => (
  <DarkBox
    as='span'
    css={{ '& svg': { width: '18px', height: '18px' } }}
    display='inline-flex'
  >
    <AiOperator strokeColor={strokeColor ?? color} />
  </DarkBox>
)

type AioEval = NonNullable<
  NonNullable<
    IncidentAioEvalResultsQuery['incident']
  >['aiOperatorRuleEvaluations']
>[number]

const PAYLOAD_KEYS = {
  yolo: '_ingress_detector_payload',
  acsRuleType: 'rule_type',
} as const

const ACS_RULE_TYPES = ['DFO', 'DHO'] as const

type CriteriaType = 'PROMPT' | 'YOLO' | 'ACS_EVENT' | 'UNKNOWN'

const DETAILS_MODAL_MAX_HEIGHT = '420px'

interface AioEvalDetailsModalIProps {
  isOpen: boolean
  onClose: () => void
  payload: Record<string, unknown>
}

const AioEvalDetailsModal = ({
  isOpen,
  onClose,
  payload,
}: AioEvalDetailsModalIProps) => {
  const formattedPayload = useMemo(
    () => JSON.stringify(payload, null, 2),
    [payload]
  )
  const { copied: hasCopied, copy: onCopy } = useClipboard({
    value: formattedPayload,
  })
  const { t } = useTranslation()
  const colorScheme = useLightMode()

  return (
    <DarkModal
      colorScheme={colorScheme}
      isOpen={isOpen}
      isSaveHidden
      onClose={onClose}
      title={t('incidents:incident_drawer_aio_eval_details_title')}
      zIndex={1600}
    >
      <DarkBox
        bg={colorScheme === 'light' ? 'light.bg.card' : 'bg.card'}
        border='1px solid'
        borderColor={
          colorScheme === 'light' ? 'light.border.default' : 'border.default'
        }
        maxH={DETAILS_MODAL_MAX_HEIGHT}
        mb='16px'
        overflow='auto'
        p='16px'
      >
        <DarkText textStyle='accent' whiteSpace='pre'>
          {formattedPayload}
        </DarkText>
      </DarkBox>
      <DarkBox display='flex' justifyContent='flex-end'>
        <DarkButton
          colorScheme={colorScheme}
          onClick={onCopy}
          variant='secondary'
        >
          {hasCopied
            ? t('common:copied')
            : t('incidents:incident_drawer_aio_eval_copy_json')}
        </DarkButton>
      </DarkBox>
    </DarkModal>
  )
}

interface AioEvalCardIProps {
  evaluation: AioEval
  onOpenDetails: (payload: Record<string, unknown>) => void
}

const getResultLabel = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  t: TFunction<any>,
  evalIsTrue?: boolean | null
) => {
  if (evalIsTrue === true) {
    return t('incidents:incident_drawer_aio_eval_matched')
  }

  if (evalIsTrue === false) {
    return t('incidents:incident_drawer_aio_eval_not_matched')
  }

  return t('incidents:incident_drawer_aio_eval_not_evaluated')
}

const getCriteriaType = (evaluation: AioEval): CriteriaType => {
  const payload = evaluation.evalPayload as Record<string, unknown> | null

  // Placeholder rows (evalPayload=null) can be of any sourceType — don't guess.
  if (!payload) {
    return 'UNKNOWN'
  }

  if (payload[PAYLOAD_KEYS.yolo]) {
    return 'YOLO'
  }

  const acsRuleType = payload[PAYLOAD_KEYS.acsRuleType]
  if (
    typeof acsRuleType === 'string' &&
    (ACS_RULE_TYPES as readonly string[]).includes(acsRuleType)
  ) {
    return 'ACS_EVENT'
  }

  return 'PROMPT'
}

const formatCreatedAt = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  t: TFunction<any>,
  createdAt?: string | null
) => {
  if (!createdAt) {
    return t('incidents:incident_drawer_aio_eval_unknown')
  }

  return new Date(createdAt).toLocaleString()
}

const getDescription = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  t: TFunction<any>,
  evaluation: AioEval
) => {
  if (evaluation.description) {
    return evaluation.description
  }

  if (
    evaluation.evalPayload &&
    typeof (evaluation.evalPayload as Record<string, unknown>).description ===
      'string'
  ) {
    return (evaluation.evalPayload as Record<string, string>).description
  }

  return t('incidents:incident_drawer_aio_eval_no_description')
}

// ─── Dark (rebrand) eval card ─────────────────────────────────────────────────

interface DarkAioEvalRowIProps {
  label: string
  value: string
  colorScheme: ColorScheme
}

const DarkAioEvalRow = ({
  label,
  value,
  colorScheme,
}: DarkAioEvalRowIProps) => {
  const isLight = colorScheme === 'light'
  return (
    <DarkText
      color={isLight ? 'light.text.primary' : 'text.primary'}
      textStyle='body'
    >
      <DarkText as='span' textStyle='body-bold'>
        {label}
      </DarkText>{' '}
      {value}
    </DarkText>
  )
}

interface DarkAioEvalCardIProps extends AioEvalCardIProps {
  colorScheme: ColorScheme
}

const DarkAioEvalCard = ({
  evaluation,
  onOpenDetails,
  colorScheme,
}: DarkAioEvalCardIProps) => {
  const { t } = useTranslation()
  const evalJson = useMemo(
    () => JSON.stringify(evaluation, null, 2),
    [evaluation]
  )
  const { copied: hasCopied, copy: onCopy } = useClipboard({ value: evalJson })
  const ruleTitle =
    evaluation.responseRule?.field?.description ??
    t('incidents:incident_drawer_aio_eval_fallback_title')
  const isLight = colorScheme === 'light'
  const prefix = isLight ? 'light.' : ''
  const isMatched = evaluation.evalIsTrue === true

  return (
    // bg.card matches the unselected CardGroup/FlashCard surface
    <DarkCard
      bg={isLight ? 'light.bg.card' : 'bg.card'}
      colorScheme={colorScheme}
      p='24px'
    >
      {/* Header row: title + badges */}
      <DarkBox
        alignItems='center'
        display='flex'
        gap='8px'
        justifyContent='space-between'
        mb='12px'
      >
        <DarkText color={`${prefix}text.primary`} textStyle='heading'>
          {ruleTitle}
        </DarkText>
        <DarkBox display='flex' flexShrink={0} gap='8px'>
          {evaluation.wasDryRun && (
            <Pill
              border='1px solid'
              borderColor={`${prefix}border.strong`}
              color={`${prefix}text.secondary`}
            >
              {t('incidents:incident_drawer_aio_eval_dry_run')}
            </Pill>
          )}
          <Pill
            bg={`${prefix}bg.chip-secondary`}
            color={`${prefix}text.chip-secondary`}
          >
            {getCriteriaType(evaluation)}
          </Pill>
        </DarkBox>
      </DarkBox>

      {/* Result */}
      <DarkText color={`${prefix}text.primary`} mb='12px' textStyle='body-bold'>
        {t('incidents:incident_drawer_aio_eval_result')}{' '}
        <DarkText
          as='span'
          color={
            isMatched
              ? `${prefix}status.success-text`
              : `${prefix}text.secondary`
          }
        >
          {getResultLabel(t, evaluation.evalIsTrue)}
        </DarkText>
      </DarkText>

      {/* Metadata rows — the label keys already include their trailing colon */}
      <DarkBox display='flex' flexDirection='column' gap='8px' mb='16px'>
        <DarkAioEvalRow
          colorScheme={colorScheme}
          label={t('incidents:incident_drawer_aio_eval_created_at')}
          value={formatCreatedAt(t, evaluation.createdAt)}
        />
        <DarkAioEvalRow
          colorScheme={colorScheme}
          label={t('incidents:incident_drawer_aio_eval_description')}
          value={getDescription(t, evaluation)}
        />
        <DarkAioEvalRow
          colorScheme={colorScheme}
          label={t('incidents:incident_drawer_aio_eval_processing_time')}
          value={
            evaluation.processingTimeSeconds != null
              ? `${evaluation.processingTimeSeconds.toFixed(1)} s`
              : t('incidents:incident_drawer_aio_eval_not_available')
          }
        />
        <DarkAioEvalRow
          colorScheme={colorScheme}
          label={t('incidents:incident_drawer_aio_eval_model')}
          value={
            evaluation.model?.canonicalName ??
            t('incidents:incident_drawer_aio_eval_no_model')
          }
        />
      </DarkBox>

      {/* Actions */}
      <DarkBox display='flex' gap='8px' justifyContent='flex-end'>
        <DarkButton
          colorScheme={colorScheme}
          onClick={onCopy}
          variant='secondary'
        >
          {hasCopied
            ? t('common:copied')
            : t('incidents:incident_drawer_aio_eval_copy_json')}
        </DarkButton>
        <DarkButton
          colorScheme={colorScheme}
          onClick={() =>
            onOpenDetails(
              (evaluation.evalPayload as Record<string, unknown>) ?? {}
            )
          }
          variant='secondary'
        >
          {t('incidents:incident_drawer_aio_eval_details_button')}
        </DarkButton>
      </DarkBox>
    </DarkCard>
  )
}

export const useAioEvalResults = (incidentId?: string) => {
  const {
    open: isResultsOpen,
    onOpen: onResultsOpen,
    onClose: onResultsClose,
  } = useDisclosure()

  const [fetchEvaluations, { data, loading: isLoading, error }] =
    useIncidentAioEvalResultsLazyQuery({
      fetchPolicy: 'network-only',
      onError: (fetchError) => {
        Sentry.captureException(fetchError, {
          tags: {
            feature: 'aio-eval-results',
          },
        })
      },
    })

  const evaluations = data?.incident?.aiOperatorRuleEvaluations ?? []

  const handleOpen = () => {
    if (!incidentId) return
    onResultsOpen()
    trackEvent(AIO_EVAL_RESULTS_OPENED, {
      incident_id: incidentId,
    })
    fetchEvaluations({ variables: { incidentId } })
  }

  return {
    isResultsOpen,
    onResultsClose,
    evaluations,
    isLoading,
    error,
    handleOpen,
  }
}

export const AioEvalResultsModals = ({
  isOpen,
  onClose,
  evaluations,
  isLoading,
  error,
}: {
  isOpen: boolean
  onClose: () => void
  evaluations: AioEval[]
  isLoading: boolean
  error?: unknown
}) => {
  const [detailsPayload, setDetailsPayload] = useState<Record<
    string,
    unknown
  > | null>(null)
  const [isDetailsOpen, setIsDetailsOpen] = useState(false)
  const { t } = useTranslation()
  const colorScheme = useLightMode()

  const handleOpenDetails = (payload: Record<string, unknown>) => {
    setDetailsPayload(payload)
    setIsDetailsOpen(true)
  }

  // The details modal is a sibling gated only on local state — reset it when
  // the results modal closes so it can't linger orphaned or reappear stale.
  const handleClose = () => {
    setIsDetailsOpen(false)
    setDetailsPayload(null)
    onClose()
  }

  // Compact (auto-height) while loading, on error, or with no evaluations —
  // the fixed-height default modal is mostly empty in those states.
  const hasResults = !isLoading && !error && evaluations.length > 0
  const isLight = colorScheme === 'light'
  return (
    <>
      <DarkModal
        colorScheme={colorScheme}
        isOpen={isOpen}
        isSaveHidden
        maxW={hasResults ? '900px' : undefined}
        onClose={handleClose}
        size={hasResults ? 'default' : 'compact'}
        title={t('incidents:incident_drawer_aio_eval_modal_title')}
        zIndex={1500}
      >
        {isLoading && (
          <DarkBox
            alignItems='center'
            display='flex'
            h='48px'
            justifyContent='center'
          >
            <DarkSpinner size='md' />
          </DarkBox>
        )}
        {!isLoading && error != null && (
          <DarkText
            color={isLight ? 'light.status.error-text' : 'status.error-text'}
            textStyle='body'
          >
            {t('incidents:incident_drawer_aio_eval_loading_error')}
          </DarkText>
        )}
        {!isLoading && !error && evaluations.length === 0 && (
          <DarkText
            color={isLight ? 'light.text.primary' : 'text.primary'}
            textStyle='body'
          >
            {t('incidents:incident_drawer_aio_eval_empty')}
          </DarkText>
        )}
        {hasResults && (
          <DarkBox display='flex' flexDirection='column' gap='16px'>
            {evaluations.map((evaluation) => (
              <DarkAioEvalCard
                colorScheme={colorScheme}
                evaluation={evaluation}
                key={evaluation.id}
                onOpenDetails={handleOpenDetails}
              />
            ))}
          </DarkBox>
        )}
      </DarkModal>

      <AioEvalDetailsModal
        isOpen={isDetailsOpen && !!detailsPayload}
        onClose={() => setIsDetailsOpen(false)}
        payload={detailsPayload ?? {}}
      />
    </>
  )
}

interface AioEvalResultsModalIProps {
  incidentId?: string
  variant?: 'fullWidth' | 'action'
}

export const AioEvalResultsButton = ({
  incidentId,
  variant = 'fullWidth',
}: AioEvalResultsModalIProps) => {
  const colorScheme = useLightMode()
  const {
    isResultsOpen,
    onResultsClose,
    evaluations,
    isLoading,
    error,
    handleOpen,
  } = useAioEvalResults(incidentId)

  return (
    <>
      {variant === 'action' ? (
        <UtilityButton
          colorScheme={colorScheme}
          data-testid='aio-eval-results-action-button'
          disabled={!incidentId}
          icon={<AiOperatorActionIcon />}
          label='AIO Evaluation Results'
          onClick={handleOpen}
          tooltipLabel='AIO Evaluation Results'
        />
      ) : (
        <DarkButton
          colorScheme={colorScheme}
          data-testid='aio-eval-results-button'
          disabled={!incidentId}
          onClick={handleOpen}
          style={{ width: '100%' }}
          variant='secondary'
        >
          View AIO Evaluation Results
        </DarkButton>
      )}

      <AioEvalResultsModals
        error={error}
        evaluations={evaluations}
        isLoading={isLoading}
        isOpen={isResultsOpen}
        onClose={onResultsClose}
      />
    </>
  )
}

export { AiOperatorActionIcon }
